import { Component, OnInit, Input, inject, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplyPanelComponent, ButtonDirective } from '@imagise/shared';
import { PictureUnit } from '../../../domain/enums/editor.enum';
import { GenericDropdownItem } from '../../../domain/interfaces/dropdown-item';
import { Size2D } from '../../../domain/interfaces/shared';
import { MessagingService } from '../../../infrastructure/messaging/messaging.service';
import { RefreshEditOptionsMessage, PreviewImageLoadedMessage } from '../../../domain/models/messages';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { EditorCropDraftOperation, EditorFormStore, isCropDraftOperation } from '../../../domain';
import { EditMode } from '../../../domain/enums/editor.enum';
import { PanelLayoutComponent } from '../panel-layout/panel-layout.component';

/** Aspect/percentage preset selectable in the crop dropdown. */
interface CropPreset {
  width: number;
  height: number;
  units: PictureUnit;
}

@Component({
  selector: 'lib-crop-panel',
  host: {
    class: 'contents',
  },
  templateUrl: './crop-panel.component.html',
  imports: [ApplyPanelComponent, ButtonDirective, MatIconModule, PanelLayoutComponent, TranslateModule],
})
export class CropPanelComponent implements OnInit {
  private readonly messageBus = inject(MessagingService);
  private readonly editorStore = inject(EditorFormStore);
  private imageSizeValue: Size2D | null = null;

  constructor() {
    this.messageBus
      .of(RefreshEditOptionsMessage).pipe(takeUntilDestroyed()).subscribe(() => this.refresh());
    this.messageBus
      .of(PreviewImageLoadedMessage).pipe(takeUntilDestroyed()).subscribe(() => this.emitSelectedModeIfNeeded());
  }

  @Input() set imageSize(value: Size2D | null) {
    if (
      this.imageSizeValue?.width === value?.width &&
      this.imageSizeValue?.height === value?.height
    ) {
      return;
    }

    this.imageSizeValue = value;
    this.refresh();
  }

  /**
   * Editor options
   */


  readonly changed = output<EditorCropDraftOperation>();

  readonly apply = output();

  readonly cancelRequested = output<void>();

  cropModes: GenericDropdownItem<CropPreset>[] = [
    {
      Name: '9:16',
      value: { width: 9, height: 16, units: PictureUnit.AspectRatio },
    },
    {
      Name: 'EDITOR.CROP.SQUARE',
      value: { width: 1, height: 1, units: PictureUnit.AspectRatio },
    },
    {
      Name: '3:2',
      value: { width: 3, height: 2, units: PictureUnit.AspectRatio },
    },
    {
      Name: '4:3',
      value: { width: 4, height: 3, units: PictureUnit.AspectRatio },
    },
    {
      Name: '5:3',
      value: { width: 5, height: 3, units: PictureUnit.AspectRatio },
    },
    {
      Name: '5:4',
      value: { width: 5, height: 4, units: PictureUnit.AspectRatio },
    },
    {
      Name: '6:5',
      value: { width: 6, height: 5, units: PictureUnit.AspectRatio },
    },
    {
      Name: '11:8',
      value: { width: 11, height: 8, units: PictureUnit.AspectRatio },
    },
    {
      Name: '14:9',
      value: { width: 14, height: 9, units: PictureUnit.AspectRatio },
    },
    {
      Name: '16:9',
      value: { width: 16, height: 9, units: PictureUnit.AspectRatio },
    },
    {
      Name: '16:10',
      value: { width: 16, height: 10, units: PictureUnit.AspectRatio },
    },
    {
      Name: 'EDITOR.CROP.CUSTOM',
      value: { width: 66, height: 66, units: PictureUnit.Percentage },
    },
  ];

  ngOnInit(): void {
    // got rid of ExpressionChangedAfterItHasBeenCheckedError
    // as per https://indepth.dev/posts/1001/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error
    setTimeout(() => {
      this.refresh();
    });
  }

  onModeSelected(item: GenericDropdownItem<CropPreset>) {
    if (item.isSelected) return;
    this.cropModes.forEach(mode => {
      mode.isSelected = mode === item;
    });

    this.changed.emit(this.toCropDraft(item.value));
  }

  showsCropIcon(mode: GenericDropdownItem<CropPreset>): boolean {
    const preset = mode.value;
    if (preset.units === PictureUnit.Percentage) {
      return false;
    }

    return preset.width !== 1 || preset.height !== 1;
  }

  private toCropDraft(preset: CropPreset): EditorCropDraftOperation {
    return {
      kind: 'crop',
      mode: EditMode.Crop,
      params: { ...preset, rect: null },
    };
  }

  onApply(): void {
    this.apply.emit();
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  private refresh(): void {
    const modeToSelect = this.resolveModeForRefresh();

    if (modeToSelect.isSelected && this.isModeApplied(modeToSelect.value)) {
      return;
    }

    modeToSelect.isSelected = false;
    this.onModeSelected(modeToSelect);
  }

  private emitSelectedModeIfNeeded(): void {
    const selectedMode = this.cropModes.find(item => item.isSelected)?.value;
    if (selectedMode && !this.isModeApplied(selectedMode)) {
      this.changed.emit(this.toCropDraft(selectedMode));
    }
  }

  private resolveModeForRefresh(): GenericDropdownItem<CropPreset> {
    return this.resolveModeFromDraft() ??
      this.cropModes.find(mode => mode.isSelected) ??
      this.resolveDefaultMode();
  }

  private resolveModeFromDraft(): GenericDropdownItem<CropPreset> | undefined {
    const draft = this.editorStore.getDraft();
    if (!isCropDraftOperation(draft)) {
      return undefined;
    }

    if (draft.params.units === PictureUnit.AspectRatio) {
      return this.findAspectMode(draft.params.width, draft.params.height) ?? this.findCustomMode();
    }

    return this.findCustomMode();
  }

  private isModeApplied(mode: CropPreset): boolean {
    const draft = this.editorStore.getDraft();
    return isCropDraftOperation(draft) &&
      draft.params.width === mode.width &&
      draft.params.height === mode.height &&
      draft.params.units === mode.units;
  }

  private resolveDefaultMode(): GenericDropdownItem<CropPreset> {
    return this.findCustomMode() ?? this.cropModes[0];
  }

  private findCustomMode(): GenericDropdownItem<CropPreset> | undefined {
    return this.cropModes.find(mode => mode.value.units === PictureUnit.Percentage);
  }

  private findAspectMode(
    width: number,
    height: number,
  ): GenericDropdownItem<CropPreset> | undefined {
    return this.cropModes.find(mode =>
      mode.value.units === PictureUnit.AspectRatio &&
      mode.value.width === width &&
      mode.value.height === height,
    );
  }
}
